import {CellClassParams, ColDef, ICellRendererParams, ValueFormatterParams, ValueGetterParams, ValueSetterParams} from 'ag-grid-community';
import * as CellRenderers from '../../../shared-modules/shared-grid/components-cell-renderers';
import {DataAdditionalNumbers} from '../../../shared-utilities/models-old/ngp-report-grid/data-additional-numbers';
import {DateUtils} from '../../../shared-utilities/utils-old/shared-utils-old/date-utils';
import {DataAdditionalIcons} from '../../../shared-utilities/models-old/ngp-report-grid/data-additional-icons';
import * as CellEditors from '../../../shared-modules/shared-grid/components-cell-editors';
import {clearableTextKeys, clearableTextValuesAreEqual, StockItem} from '../../../shared-utilities/models-old/datastructures';
import {
  CELL_RENDERER_CURRENCY_STOCK_MANAGER,
  CELL_RENDERER_TEXT_STOCK_MANAGER,
} from '../../../shared-modules/shared-grid/models/js-cell-renderers';
import {
  STOCK_MANAGER_CELL_EDITOR_MULTIPLE_BARCODES,
  STOCK_MANAGER_CELL_RENDERER_MULTIPLE_BARCODES,
} from './stock-manager-grid-cell-components';
import {multipleBarcodesValuesAreEqual, normalizeStockItemTagValues} from '../utils/stock-manager-barcode.utils';
import {
  stockItemHasMultiplePriceChanges,
} from '../../../shared/shared-utils/price/multiple-price-change.utils';
import {
  getDepartmentDisplayName,
  getSubDepartmentDisplayName,
} from '../../../shared/shared-utils/stock/department-display.utils';

import {TLocaleDateFormat} from '../../settings-new/models';
import {getMonthlyAverage} from '../../../shared/shared-utils/calculations/calculations-stock';
import {editableFields} from './stock-manager-editable-fields';

export {editableFields};

const costMissTooltip = 'Latest cost is not the same as last GRV price.';
const itemRecentlyCreatedTooltip = 'Item was recently created.';
const itemEditedTooltip = 'Item was marked as edited.';
const itemErrorTooltip = `Item has an error and can't be submitted`;
const multiplePriceChangeTooltip = 'Other sell prices changed with retail price 1. Click to see them.';

export const stockManagerGridColDefs: ColDef[] = [
  // IconsEnum ------------------------------
  {
    field: 'icons',
    headerName: '',
    hide: false,
    pinned: 'left',
    lockVisible: true,
    resizable: true,
    lockPinned: true,
    sortable: false,
    lockPosition: true,
    cellClass: 'grid-cell-icons',
    cellRenderer: CellRenderers.CELL_RENDERER_ICONS,
    cellRendererParams: {
      dataAdditional: {
        isCostMissIconVisible: true,
        costMissIconTooltip: costMissTooltip,
        isItemRecentlyCreatedIconVisible: true,
        itemRecentlyCreatedIconTooltip: itemRecentlyCreatedTooltip,
        isItemEditedIconVisible: true,
        itemEditedIconTooltip: itemEditedTooltip,
        isErrorIconVisible: true,
        itemErrorIconTooltip: itemErrorTooltip,
        isMultiplePriceChangeIconVisible: true,
        multiplePriceChangeIconTooltip: multiplePriceChangeTooltip,
      } as DataAdditionalIcons,
    },
    // The column has no value of its own, so AG Grid would never refresh the cell when the row
    // data changes. Expose the icon state as the cell value so edits re-render the icons.
    valueGetter: (params: ValueGetterParams<StockItem>): string => {
      const data = params.data as unknown as Record<string, unknown> | undefined;
      return [
        data?.['isEdited'] ?? false,
        data?.['costMiss'] ?? false,
        data?.['error'] ?? false,
        stockItemHasMultiplePriceChanges(data),
      ].join('|');
    },
    width: 90,
  },
  // General ----------------------------
  {
    field: 'code',
    headerName: 'Code',
    hide: false,
    pinned: 'left',
    resizable: true,
    lockVisible: true,
    lockPinned: true,
    lockPosition: true,
    editable: false,
    cellEditor: false,
    cellRenderer: CellRenderers.CELL_RENDERER_DISABLE_ITEM,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
    },
    width: 120,
  },
  {
    field: 'desc',
    headerName: 'Description',
    pinned: 'left',
    hide: false,
    sortable: false,
    lockPinned: true,
    resizable: true,
    editable: false,
    lockVisible: true,
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CELL_RENDERER_TEXT_STOCK_MANAGER,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 300,
  },
  {
    field: 'alternateDescription',
    headerName: 'Alternative Description',
    hide: true,
    sortable: false,
    lockPinned: true,
    lockVisible: false,
    resizable: true,
    editable: false,
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CELL_RENDERER_TEXT_STOCK_MANAGER,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return !clearableTextValuesAreEqual(
          params.data.originalValue?.[pos]?.value,
          params.data[pos],
        );
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 300,
  },
  {
    field: 'barcode',
    headerName: 'Barcode',
    hide: true,
    lockPinned: true,
    resizable: true,
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CELL_RENDERER_TEXT_STOCK_MANAGER,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return !clearableTextValuesAreEqual(
          params.data.originalValue?.[pos]?.value,
          params.data[pos],
        );
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 160,
  },
  {
    field: 'multipleBarcodes',
    headerName: 'Additional Barcodes',
    hide: true,
    lockPinned: true,
    resizable: true,
    valueSetter: (params: ValueSetterParams): boolean => {
      const normalized = Array.isArray(params.newValue)
        ? params.newValue
        : String(params.newValue ?? '')
          .split(',')
          .map((value: string) => value.trim())
          .filter((value: string) => value.length > 0);
      params.data[params.colDef.field] = normalized;
      return true;
    },
    cellEditor: STOCK_MANAGER_CELL_EDITOR_MULTIPLE_BARCODES,
    cellRenderer: STOCK_MANAGER_CELL_RENDERER_MULTIPLE_BARCODES,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const field = params.column.getColDef().field;
        return !multipleBarcodesValuesAreEqual(
          params.data?.[field],
          params.data?.originalValue?.[field]?.value,
        );
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 180,
  },
  {
    field: 'binL',
    headerName: 'Bin Location',
    hide: true,
    lockPinned: true,
    resizable: true,
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CELL_RENDERER_TEXT_STOCK_MANAGER,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 120,
  },
  {
    field: 'lastPurchase',
    headerName: 'Last Purchased',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    valueFormatter: (params: ValueFormatterParams<StockItem, string | number | Date>): string => {
      const date = new Date(params.value);

      if (isNaN(date.getTime())) {
        return 'No Date Found';
      } else {
        const settings = params.context['dateSettings'] as TLocaleDateFormat;
        return DateUtils.dateToString(params.value as unknown as Date, settings);
      }
    },
    width: 150,
  },
  {
    field: 'created',
    headerName: 'Created',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    valueFormatter: (params: ValueFormatterParams<StockItem, string | number | Date>): string => {
      const date = new Date(params.value);
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return 'No Date Found';
      } else {
        const settings = params.context['dateSettings'] as TLocaleDateFormat;
        return DateUtils.dateToString(params.value as unknown as Date, settings);
      }
    },
    width: 150,
  },
  {
    field: 'lastSold',
    headerName: 'Last Sold',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    valueFormatter: (params: ValueFormatterParams<StockItem, string | number | Date>): string => {
      const date = new Date(params.value);
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return 'No Date Found';
      } else {
        const settings = params.context['dateSettings'] as TLocaleDateFormat;
        return DateUtils.dateToString(params.value as unknown as Date, settings);
      }
    },
    width: 150,
  },
  {
    field: 'dlvColl',
    headerName: 'DelCol',
    hide: true,
    lockPinned: true,
    resizable: true,
    cellEditor: false,
    width: 100,
  },
  {
    field: 'dep',
    headerName: 'Department',
    hide: false,
    lockPinned: true,
    resizable: true,
    cellEditor: CellEditors.CELL_EDITOR_SELECT_DROPDOWN,
    cellRenderer: CellRenderers.CELL_RENDERER_DEPARTMENTS,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 175,
  },
  {
    field: 'depName',
    headerName: 'Department Name',
    hide: false,
    lockPinned: true,
    resizable: true,
    sortable: false,
    cellEditor: false,
    cellRenderer: CellRenderers.CELL_RENDERER_DEPARTMENTS,
    valueGetter: (params: ValueGetterParams<StockItem>): string => getDepartmentDisplayName(params.data?.dep),
    valueFormatter: (params: ValueFormatterParams<StockItem>): string =>
      params.value ?? getDepartmentDisplayName(params.data?.dep),
    width: 175,
  },
  {
    field: 'subDep',
    headerName: 'Sub Department',
    hide: true,
    lockPinned: true,
    resizable: true,
    cellEditor: CellEditors.CELL_EDITOR_SELECT_DROPDOWN,
    cellRenderer: CellRenderers.CELL_RENDERER_DEPARTMENTS,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 175,
  },
  {
    field: 'subDepName',
    headerName: 'Sub Department Name',
    hide: true,
    lockPinned: true,
    resizable: true,
    sortable: false,
    cellEditor: false,
    cellRenderer: CellRenderers.CELL_RENDERER_DEPARTMENTS,
    valueGetter: (params: ValueGetterParams<StockItem>): string => getSubDepartmentDisplayName(params.data?.subDep),
    valueFormatter: (params: ValueFormatterParams<StockItem>): string =>
      params.value ?? getSubDepartmentDisplayName(params.data?.subDep),
    width: 175,
  },
  {
    field: 'gp1',
    headerName: 'GP % (Avg Cost)',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellEditor: false,
    cellRendererParams: {dataAdditional: {decimalPlaces: 2} as DataAdditionalNumbers},
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 150,
  },
  {
    field: 'suppUsedLastDate1',
    headerName: 'GRV Date',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    valueFormatter: (params: ValueFormatterParams<StockItem, string | number | Date>): string => {
      const date = new Date(params.value);
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return 'No Date Found';
      } else {
        const settings = params.context['dateSettings'] as TLocaleDateFormat;
        return DateUtils.dateToString(params.value as unknown as Date, settings);
      }
    },
    width: 175,
  },
  {
    field: 'genCode',
    headerName: 'General Code',
    hide: true,
    resizable: true,
    lockPinned: true,
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CELL_RENDERER_TEXT_STOCK_MANAGER,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 140,
  },
  {
    field: 'latestCost',
    headerName: 'Latest Cost',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellEditor: false,
    cellRendererParams: {dataAdditional: {decimalPlaces: 2} as DataAdditionalNumbers},
    width: 120,
  },
  {
    field: 'onHand',
    headerName: 'On Hand',
    hide: false,
    lockPinned: true,
    type: ['alignRight'],
    cellEditor: false,
    width: 100,
  },
  {
    field: 'stockValue',
    headerName: 'Stock Value',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellEditor: false,
    cellRenderer: CELL_RENDERER_CURRENCY_STOCK_MANAGER,
    cellRendererParams: {dataAdditional: {decimalPlaces: 2} as DataAdditionalNumbers},
    width: 130,
  },
  {
    field: 'nominalGP',
    headerName: 'Current GP %',
    hide: false,
    lockPinned: true,
    lockVisible: true,
    resizable: true,
    type: ['alignRight'],
    cellEditor: CellEditors.CELL_EDITOR_PRICE_NOMINAL_GP,
    cellRenderer: CELL_RENDERER_CURRENCY_STOCK_MANAGER,
    cellRendererParams: {dataAdditional: {decimalPlaces: 2} as DataAdditionalNumbers},
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 160,
  },
  {
    field: 'onHoldCode',
    headerName: 'On Hold Code',
    hide: true,
    lockPinned: true,
    resizable: true,
    cellEditor: CellEditors.CELL_EDITOR_MULTI_SELECT_ON_HOLD,
    cellRenderer: CellRenderers.CELL_RENDERER_MULTI_SELECT,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 140,
  },
  {
    field: '_tags',
    headerName: 'Tags',
    hide: true,
    lockPinned: true,
    resizable: true,
    valueSetter: (params: ValueSetterParams): boolean => {
      params.data = {
        ...params.data,
        ['_tags']: normalizeStockItemTagValues(params.newValue),
      };
      return true;
    },
    cellEditor: CellEditors.CELL_EDITOR_MULTI_SELECT_ARRAY,
    cellRenderer: CellRenderers.CELL_RENDERER_MULTI_SELECT_ARRAY,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        const arraysAreEqual = (arr1: string[], arr2: string[]): boolean => {
          if (!arr1 || !arr2) {
            return false;
          }
          if (arr1.length !== arr2?.length) {
            return false;
          }
          return arr1.every((value, index) => {
            return value === arr2[index];
          });
        };
        if (typeof params.data[pos] === 'string') {
          params.data[pos] = params.data[pos].split(',');
        }
        return !arraysAreEqual(
          params?.data?.[pos]?.filter((item: string) => item !== '') as string[],
          params?.data?.originalValue?.[pos]?.value as unknown as string[],
        );
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 100,
  },
  {
    field: 'ordLvl',
    headerName: 'Order Level',
    hide: true,
    lockPinned: true,
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CELL_RENDERER_CURRENCY_STOCK_MANAGER,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 120,
  },
  {
    field: 'maxOrderLevel',
    headerName: 'Max Order Level',
    hide: true,
    lockPinned: true,
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CELL_RENDERER_CURRENCY_STOCK_MANAGER,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 100,
  },
  {
    field: 'orderQty',
    headerName: 'Order Quantity',
    hide: true,
    lockPinned: true,
    sortable: false,
    type: ['alignRight'],
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: (params: ICellRendererParams<StockItem>): HTMLElement | string =>
      params.value == null ? 'undefined' : CELL_RENDERER_CURRENCY_STOCK_MANAGER(params),
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 100,
  },
  {
    field: 'lastStockTake',
    headerName: 'Last Stock Take',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    valueFormatter: (params: ValueFormatterParams<StockItem, string | number | Date>): string => {
      const date = new Date(params.value);
      if (isNaN(date.getTime())) {
        return 'No Date Found';
      } else {
        const settings = params.context['dateSettings'] as TLocaleDateFormat;
        return DateUtils.dateToString(params.value as unknown as Date, settings);
      }
    },
    width: 150,
  },
  {
    field: 'modified',
    headerName: 'Modified',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    valueFormatter: (params: ValueFormatterParams<StockItem, string | number | Date>): string => {
      const date = new Date(params.value);
      if (isNaN(date.getTime())) {
        return 'No Date Found';
      } else {
        const settings = params.context['dateSettings'] as TLocaleDateFormat;
        return DateUtils.dateToString(params.value as unknown as Date, settings);
      }
    },
    width: 170,
  },
  {
    field: 'minOrderLevel',
    headerName: 'Min Order Level',
    hide: true,
    lockPinned: true,
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CELL_RENDERER_CURRENCY_STOCK_MANAGER,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 100,
  },
  {
    field: 'packSize',
    headerName: 'Pack Size',
    hide: true,
    lockPinned: true,
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CELL_RENDERER_CURRENCY_STOCK_MANAGER,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 100,
  },
  {
    field: 'prvSellingPri',
    headerName: 'Prev Retail Price',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellEditor: false,
    cellRendererParams: {dataAdditional: {decimalPlaces: 2} as DataAdditionalNumbers},
    width: 160,
  },
  {
    field: 'sellPriIncl1',
    headerName: 'Retail Price',
    hide: false,
    lockPinned: true,
    resizable: true,
    lockVisible: true,
    type: ['alignRight'],
    cellEditor: CellEditors.CELL_EDITOR_PRICE_NOMINAL_GP,
    cellRenderer: CELL_RENDERER_CURRENCY_STOCK_MANAGER,
    cellRendererParams: {dataAdditional: {decimalPlaces: 2} as DataAdditionalNumbers},
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return +params.data.originalValue?.[pos]?.value !== +params.data[pos];
      },
    },
    width: 130,
  },
  {
    field: 'purchaseOrder',
    headerName: 'Purchase Order',
    hide: true,
    lockPinned: true,
    resizable: true,
    cellEditor: false,
    width: 150,
  },
  {
    field: 'recommendedGP',
    headerName: 'Target GP %',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CELL_RENDERER_CURRENCY_STOCK_MANAGER,
    cellRendererParams: {dataAdditional: {decimalPlaces: 2} as DataAdditionalNumbers},
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 100,
  },
  {
    field: 'suppCode',
    headerName: 'Supplier Code',
    hide: true,
    lockPinned: true,
    resizable: true,
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CELL_RENDERER_TEXT_STOCK_MANAGER,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 130,
  },
  {
    field: 'regularSuppCode',
    headerName: 'Supplier',
    hide: false,
    lockPinned: true,
    resizable: true,
    cellEditor: CellEditors.CELL_EDITOR_SUPPLIER,
    cellRenderer: CELL_RENDERER_TEXT_STOCK_MANAGER,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 100,
  },
  {
    field: 'multipleSuppliers',
    headerName: 'Other Suppliers',
    hide: true,
    lockPinned: true,
    resizable: true,
    cellEditor: CellEditors.CELL_EDITOR_SUPPLIER_MULTI_SELECT,
    cellRenderer: CELL_RENDERER_TEXT_STOCK_MANAGER,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        const arraysAreEqual = (arr1: string[], arr2: string[]): boolean => {
          if (!arr1 || !arr2) {
            return false;
          }
          if (arr1.length !== arr2.length) {
            return false;
          }
          return arr1.every((value, index) => {
            return value === arr2[index];
          });
        };
        if (typeof params.data[pos] === 'string') {
          params.data[pos] = params.data[pos].split(',');
        }
        return !arraysAreEqual(
          params?.data?.[pos]?.filter((item: string) => item !== '') as string[],
          params?.data?.originalValue?.[pos]?.value as unknown as string[],
        );
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 140,
  },
  {
    field: 'lineColourCode',
    headerName: 'Line Colour',
    hide: true,
    lockPinned: true,
    resizable: true,
    cellEditor: CellEditors.CELL_EDITOR_SELECT_DROPDOWN,
    cellRenderer: CellRenderers.CELL_RENDERER_LINE_COLOUR,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<StockItem>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<StockItem>): boolean => {
        return params.data.disabled;
      },
    },
    width: 140,
  },
  {
    field: 'unitsYear',
    headerName: 'Units 12 Months',
    hide: false,
    lockVisible: true,
    lockPinned: true,
    type: ['alignRight'],
    cellEditor: false,
    valueFormatter: (params: ValueFormatterParams<StockItem>): string => {
      if (isNaN(params.data.unitsYear)) {
        return '0';
      }
      return `${params.data.unitsYear}`;
    },
    resizable: true,
    width: 150,
  },
  {
    field: 'unitsMonthAvg',
    headerName: 'Monthly Avg',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellEditor: false,
    valueGetter: (params: ValueGetterParams<StockItem>): string => getMonthlyAverage(params.data),
    tooltipValueGetter: (): string => 'Average units sold per month (last 12 months)',
    resizable: true,
    width: 120,
  },
];

/** Simple deep-equals for two `string[]`s */
export function arraysEqual(a?: string[], b?: string[]): boolean {
  if (!a || !b || a.length !== b.length) {
    return false;
  }
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Is the cell edited vs. its originalValue? */
export function isEdited(p: ICellRendererParams): boolean {
  const field = p.colDef.field;
  const orig = p.data?.originalValue?.[field]?.value;
  const cur = p.data?.[field] ?? p.value;
  if (field === 'multipleBarcodes') {
    return !multipleBarcodesValuesAreEqual(cur, orig);
  }
  if (clearableTextKeys.includes(field)) {
    return !clearableTextValuesAreEqual(cur, orig);
  }
  return Array.isArray(cur)
    ? !arraysEqual(cur.filter(Boolean) as unknown as string[], orig as unknown as string[])
    : cur !== orig;
}
